import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormikContext, Field, FieldArray } from "formik";
import { TextField, RadioGroup } from "formik-material-ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import SupplierQuestions from "../Suppliers/Questions/SupplierQuestions";

import ModelFormSelect from "../../themes/components/ModelFormSelect";
import Tooltip from "../../themes/components/Tooltip";
import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";

import DeleteIcon from "../../components/Icons/DeleteIcon";
import CustomCheckbox from "../Form/CustomCheckbox";
import { notifySimple } from "../../themes/components/Notifications";
import { countryCodes } from "../../types/countryCodes";
import { Chip, InputAdornment, InputLabel, Radio } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoCompleteDropdown from "../../themes/components/AutoCompleteDropdown";
import CountryFilterDropdown from "../../components/CountryFilterDropdown";
import { COLORS } from "../../themes/Base";
import { statusOptions } from "../../constants/project";
import SubmittingFormBackdrop from "../../components/SubmittingFormBackdrop";
import { color } from "@mui/system";

type Props = {
  suppliers: Supplier[];
  projectManagers: { id: string; first_name: string; last_name: string }[];
};

const ProjectForm: React.FC<Props> = ({ suppliers, projectManagers }) => {
  const navigate = useNavigate();
  const { values, setFieldValue, handleSubmit, errors, submitCount, isSubmitting } = useFormikContext<ProjectCreateVars>();
  const [newSupplierId, setNewSupplierId] = useState<Supplier["id"]>("");
  const [expandedIdx, setExpandedIdx] = useState<number | false>(0);
  const [innerExpanded, setInnerExpanded] = useState<{ [key: number]: string | false }>({});
  const [countryFilter, setCountryFilter] = useState<string[]>([]);

  const { classes } = useSharedStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();
  const isEditMode = Boolean(values?.id);
  const projectNameRef = useRef<HTMLInputElement>(null);
  const completedResponsesLimitRef = useRef<HTMLInputElement>(null);

  const handleFieldClick = (supplierIdx: number, supplier_field_id: string) => {
    const project_suppliers = values.input.suppliers || [];
    const existingField = project_suppliers[supplierIdx].fields.find(
      (f) => f.supplier_field_id === supplier_field_id
    );
    if (existingField) {
      if (existingField.id) {
        // already saved field, toggle deleted param
        const fieldIdx = project_suppliers[supplierIdx].fields.indexOf(existingField);
        if (fieldIdx !== -1) {
          setFieldValue(
            `input.suppliers.${supplierIdx}.fields.${fieldIdx}.deleted`,
            !existingField.deleted
          );
        }
      } else {
        setFieldValue(
          `input.suppliers.${supplierIdx}.fields`,
          project_suppliers[supplierIdx].fields.filter(
            (f) => f.supplier_field_id !== supplier_field_id
          )
        );
      }
    } else {
      setFieldValue(`input.suppliers.${supplierIdx}.fields`, [
        ...project_suppliers[supplierIdx].fields,
        { supplier_field_id: supplier_field_id },
      ]);
    }
  };

  const updateSupplierQuestions = (inputName: string, questions: ProjectSupplierQuestion[]) => {
    setFieldValue(inputName, questions);
  };

  useEffect(() => {
    if (submitCount > 0 && errors?.input?.name && projectNameRef.current) {
      projectNameRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      projectNameRef.current.focus();
    }
    if (submitCount > 0 && errors?.input?.completed_responses_limit && completedResponsesLimitRef.current) {
      completedResponsesLimitRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      completedResponsesLimitRef.current.focus();
    }
  }, [errors, submitCount]);

  const filteredCountryCodes = values.input.suppliers
    ? countryCodes.filter((cc) =>
      values.input.suppliers.filter((s) => !s.deleted).some((s) => s.country_code === cc.code)
    )
    : [];

  const renderStatusChip = (status) => (
    <Chip
      label={status.label}
      sx={{
        background: status.bgcolor,
        color: status.color,
        fontSize: 14,
        height: 24,
      }}
    />
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        mt: 12,
      }}
    >
      <SubmittingFormBackdrop open={!!isSubmitting} message="Saving..." />
      <Box
        className={classes.contentBox}
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: "80px", // Height of the footer
        }}
      >
        <Typography variant="h2">Project Details</Typography>
        <Box className={classes.fieldBox}>
          <InputLabel>Project Name *</InputLabel>
          <Field
            inputRef={projectNameRef}
            component={TextField}
            name="input.name"
            placeholder="Project Name"
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box className={classes.fieldBox}>
          <InputLabel>Completed Responses Limit *</InputLabel>
          <Field
            inputRef={completedResponsesLimitRef}
            component={TextField}
            name="input.completed_responses_limit"
            placeholder="Completed Responses Limit"
            variant="outlined"
            type="number"
            fullWidth
          />
        </Box>
        <Box className={classes.fieldBox}>
          <InputLabel>Status</InputLabel>
          <ModelFormSelect
            fullWidth
            variant="outlined"
            value={values.input.status || ""}
            onChange={(e) => {
              setFieldValue(`input.status`, e.target.value);
            }}
            MenuProps={{
              PaperProps: {
                className: classes.innerBox,
                sx: { fontSize: 14 },
              },
            }}
          >
            {statusOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value?.toUpperCase()}
                sx={{
                  py: 0.8,
                  "&.Mui-selected, &:hover, &.Mui-selected:hover": {
                    backgroundColor: option.bgcolor,
                    color: "inherit",
                    borderRadius: "8px",
                  },
                }}
              >
                {renderStatusChip(option)}
              </MenuItem>
            ))}
          </ModelFormSelect>
        </Box>
        <Box className={classes.fieldBox}>
          <InputLabel>Project Manager</InputLabel>
          <ModelFormSelect
            placeholder="Project Manager"
            fullWidth
            variant="outlined"
            value={values.input.account_id || ""}
            onChange={(e) => {
              setFieldValue(`input.account_id`, e.target.value);
            }}
            MenuProps={{
              PaperProps: {
                className: classes.innerBox,
                sx: { fontSize: 14 },
              },
            }}
          >
            {projectManagers.map((pm) => (
              <MenuItem key={pm.id} value={pm.id}>
                {pm.first_name} {pm.last_name}
              </MenuItem>
            ))}
          </ModelFormSelect>
        </Box>
        <Box className={classes.fieldBox}>
          <FormControlLabel
            style={{ color: "black" }}
            label="Exempt from auto purging"
            control={
              <CustomCheckbox
                sx={{ py: 0 }}
                checked={values.input.auto_purge_exempt || false}
                onChange={() => setFieldValue(`input.auto_purge_exempt`, !values.input.auto_purge_exempt)}
              />
            }
            onChange={() => setFieldValue(`input.auto_purge_exempt`, !values.input.auto_purge_exempt)}
          />
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h2" sx={{ mr: 2, pt: 2 }}>
            Suppliers
          </Typography>
          <CountryFilterDropdown
            value={countryFilter}
            onChange={setCountryFilter}
            options={filteredCountryCodes}
            supplierCount={(code) =>
              values.input.suppliers?.filter((s) => s.country_code === code && !s.deleted).length ?? 0
            }
          />
        </Box>
        <FieldArray
          name="input.suppliers"
          render={(arrayHelpers) => (
            <>
              {values.input.suppliers
                ?.filter(
                  (projectSupplier) =>
                    countryFilter.length === 0 ||
                    (typeof projectSupplier.country_code === "string" &&
                      countryFilter.includes(projectSupplier.country_code))
                )
                ?.map((projectSupplier, sIdx) =>
                  projectSupplier.deleted ? (
                    <div key={sIdx}></div>
                  ) : (
                    <Accordion
                      key={sIdx}
                      onChange={() => setExpandedIdx(expandedIdx === sIdx ? false : sIdx)}
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #E4E8D3",
                        background: "#FFF",
                        boxShadow: "none",
                        marginBottom: 2,
                        "&.Mui-expanded": {
                          margin: 0,
                          mb: 2,
                        },
                      }}
                      defaultExpanded={!isEditMode && sIdx === 0}
                    >
                      <AccordionSummary
                        aria-controls={`supplier-panel-${sIdx}-content`}
                        id={`supplier-panel-${sIdx}-header`}
                        sx={{
                          border: "none",
                          boxShadow: "none",
                          padding: "16px 24px",
                          m: 0,
                          "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
                            margin: 0,
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Box display="flex" alignItems="center">
                            <Typography variant="h3" sx={{ p: 0 }}>
                              {suppliers.find((sup) => sup.id === projectSupplier.supplier_id)?.name || ""}
                              <Box component="span" sx={{ color: "#67676C", pl: 1 }}>
                                {countryCodes.find(
                                  (cc) => cc.code === projectSupplier.country_code
                                )?.name}
                              </Box>
                            </Typography>
                            <ExpandMoreIcon
                              sx={{
                                transition: "transform 0.2s",
                                transform: expandedIdx === sIdx ? "rotate(180deg)" : "rotate(0deg)",
                                ml: 1,
                              }}
                            />
                          </Box>
                          <Tooltip title="Delete" placement="top">
                            <IconButton
                              onClick={() => {
                                if (!projectSupplier.id) {
                                  arrayHelpers.remove(sIdx);
                                } else {
                                  setFieldValue(`input.suppliers.${sIdx}.deleted`, true);
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ borderTop: "1px solid #E4E8D3" }}>
                        <Box key={sIdx} display="flex" alignItems="flex-start">
                          <Box width="100%">
                            <Box className={classes.fieldBox}>
                              <InputLabel>Description</InputLabel>
                              <Field
                                component={TextField}
                                name={`input.suppliers.${sIdx}.description`}
                                variant="outlined"
                                fullWidth
                                style={{ marginRight: 8 }}
                                placeholder="Description"
                              />
                            </Box>
                            <Box className={classes.fieldBox}>
                              <InputLabel>Country Code</InputLabel>
                              <AutoCompleteDropdown
                                label="Country Code"
                                name={`input.suppliers.${sIdx}.country_code`}
                                value={countryCodes.find(cc => cc.code === projectSupplier.country_code) || ""}
                                options={countryCodes}
                                onChange={(code) => setFieldValue(`input.suppliers.${sIdx}.country_code`, code)}
                                getOptionLabel={(option) => `${option.name} - Country Code: ${option.code}`}
                                getOptionValue={(option) => option?.code ?? ""}
                                placeholder="Country Code"
                              />
                            </Box>
                            <Box className={classes.fieldBox}>
                              <InputLabel>Survey base URL</InputLabel>
                              <Field
                                component={TextField}
                                name={`input.suppliers.${sIdx}.survey_url`}
                                variant="outlined"
                                fullWidth
                                style={{ marginRight: 8 }}
                                placeholder="Survey base URL"
                              />
                            </Box>
                            <Box className={classes.fieldBox}>
                              <InputLabel>Responses limit</InputLabel>
                              <Field
                                component={TextField}
                                name={`input.suppliers.${sIdx}.limit`}
                                variant="outlined"
                                type="number"
                                fullWidth
                              />
                            </Box>
                            <Box className={classes.fieldBox}>
                              <InputLabel>Device Forensiq Score Threshold</InputLabel>
                              <Field
                                component={TextField}
                                name={`input.suppliers.${sIdx}.score`}
                                variant="outlined"
                                type="number"
                                value={projectSupplier.score ?? "25"}
                                fullWidth
                              />
                            </Box>
                            <Box className={classes.fieldBox}>
                              <InputLabel>Roundtable AI Proof of Human Score Threshold</InputLabel>
                              <Field
                                component={TextField}
                                name={`input.suppliers.${sIdx}.rt_proof_of_human_threshold`}
                                variant="outlined"
                                type="number"
                                value={projectSupplier.rt_proof_of_human_threshold ?? "60"}
                                fullWidth
                              />
                            </Box>
                            <div>
                              <input
                                type="hidden"
                                name={`input.suppliers.${sIdx}.supplier_id`}
                                value={projectSupplier.supplier_id}
                              />
                            </div>
                            <Accordion
                              expanded={innerExpanded[sIdx] === "url"}
                              onChange={() =>
                                setInnerExpanded((prev) => ({
                                  ...prev,
                                  [sIdx]: prev[sIdx] === "url" ? false : "url",
                                }))
                              }
                              sx={{
                                mb: 2,
                                boxShadow: "none",
                                borderRadius: 2,
                                border: "1px solid #E4E8D3",
                                "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
                                  margin: 0,
                                },
                              }}
                            >
                              <AccordionSummary sx={{ padding: "16px 24px" }}>
                                <Typography variant="h3" sx={{ p: 0 }}>URL Requirements</Typography>
                                <ExpandMoreIcon
                                  sx={{
                                    transition: "transform 0.2s",
                                    transform: innerExpanded[sIdx] === "url" ? "rotate(180deg)" : "rotate(0deg)",
                                    ml: 1,
                                  }}
                                />
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box px={1.2}>
                                  Survey redirect URLs dynamic placeholders markup
                                  <Typography variant="subtitle1">
                                    These should be provided by the Survey Tool to wrap any dynamic URL variable,
                                    for example <code>{"{{RID}}"}</code>
                                  </Typography>
                                  <Box display="flex" justifyContent="space-between" gap={2}>
                                    <Box className={classes.fieldBox} width="100%">
                                      <InputLabel>Opening tag</InputLabel>
                                      <Field
                                        component={TextField}
                                        name={`input.suppliers.${sIdx}.opening_tag`}
                                        variant="outlined"
                                        fullWidth
                                      />
                                    </Box>
                                    <Box className={classes.fieldBox} width="100%">
                                      <InputLabel>Closing tag</InputLabel>
                                      <Field
                                        component={TextField}
                                        name={`input.suppliers.${sIdx}.closing_tag`}
                                        variant="outlined"
                                        fullWidth
                                      />
                                    </Box>
                                  </Box>
                                  <Typography variant="h3" sx={{ py: 2 }}>URL Variable Mapping</Typography>
                                  <Box display="flex" justifyContent="space-between" gap={2}>
                                    <Box className={classes.fieldBox} width="100%">
                                      <InputLabel>obsrv_id</InputLabel>
                                      <Field
                                        component={TextField}
                                        name={`input.suppliers.${sIdx}.respondent_key`}
                                        variant="outlined"
                                        fullWidth
                                      />
                                    </Box>
                                    <Box className={classes.fieldBox} width="100%">
                                      <InputLabel>obsrv_v</InputLabel>
                                      <Field
                                        component={TextField}
                                        name={`input.suppliers.${sIdx}.status_key`}
                                        variant="outlined"
                                        fullWidth
                                      />
                                    </Box>
                                  </Box>
                                  <Typography variant="h3" sx={{ py: 2 }}>Fields</Typography>
                                  {suppliers
                                    .find((sup) => sup.id === projectSupplier.supplier_id)
                                    ?.fields.map((supplier_field) => (
                                      <div key={supplier_field.id}>
                                        <FormControlLabel
                                          disabled={
                                            supplier_field.required &&
                                            projectSupplier.fields
                                              .filter((f) => !f.deleted)
                                              .map((f) => f.supplier_field_id as string)
                                              .includes(supplier_field.id)
                                          }
                                          label={supplier_field.name || ""}
                                          control={
                                            <CustomCheckbox
                                              sx={{ py: 0 }}
                                              color="secondary"
                                              checked={projectSupplier.fields
                                                .filter((f) => !f.deleted)
                                                .map((f) => f.supplier_field_id as string)
                                                .includes(supplier_field.id)}
                                              onChange={() => handleFieldClick(sIdx, supplier_field.id)}
                                            />
                                          }
                                          onChange={() => handleFieldClick(sIdx, supplier_field.id)}
                                        />
                                      </div>
                                    ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                            <Accordion
                              expanded={innerExpanded[sIdx] === "pre"}
                              onChange={() =>
                                setInnerExpanded((prev) => ({
                                  ...prev,
                                  [sIdx]: prev[sIdx] === "pre" ? false : "pre",
                                }))
                              }
                              sx={{
                                mb: 2,
                                boxShadow: "none",
                                borderRadius: 2,
                                border: "1px solid #E4E8D3",
                                "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
                                  margin: 0,
                                },
                              }}
                            >
                              <AccordionSummary sx={{ padding: "16px 24px" }}>
                                <Typography variant="h3" sx={{ p: 0 }}>Pre-Survey Questions</Typography>
                                <ExpandMoreIcon
                                  sx={{
                                    transition: "transform 0.2s",
                                    transform: innerExpanded[sIdx] === "pre" ? "rotate(180deg)" : "rotate(0deg)",
                                    ml: 1,
                                  }}
                                />
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box px={1.2}>
                                  <Box mb={1}>Pre Survey Questions</Box>
                                  <Box className={classes.fieldBox} width="100%">
                                    <InputLabel>Pre-Survey Id</InputLabel>
                                    <Field
                                      component={TextField}
                                      name={`input.suppliers.${sIdx}.pre_survey_id`}
                                      placeholder="Pre-Survey Id"
                                      variant="outlined"
                                      type="text"
                                      fullWidth
                                      value={projectSupplier.pre_survey_id ?? ""}
                                    />
                                  </Box>
                                  <Box className={classes.fieldBox} width="100%">
                                    <InputLabel>Pre-Survey Tool</InputLabel>
                                    <Field
                                      component={RadioGroup}
                                      name={`input.suppliers.${sIdx}.pre_survey_tool`}
                                      row
                                      label="Pre-Survey Tool"
                                      value={projectSupplier.pre_survey_tool ?? ""}
                                    >
                                      <FormControlLabel value="" control={<Radio color="secondary" />} label="None" />
                                      <FormControlLabel value="roundtable" control={<Radio color="secondary" />} label="Roundtable AI" />
                                    </Field>
                                  </Box>
                                  {projectSupplier.pre_survey_tool === "roundtable" && (
                                    <Box className={classes.fieldBox} width="100%" ml={1}>
                                      <FormControlLabel
                                        control={
                                          <CustomCheckbox
                                            sx={{ py: 0 }}
                                            name={`input.suppliers.${sIdx}.ai_validation`}
                                            checked={projectSupplier.ai_validation ?? false}
                                            onChange={() =>
                                              setFieldValue(
                                                `input.suppliers.${sIdx}.ai_validation`,
                                                !values.input.suppliers?.[sIdx].ai_validation
                                              )
                                            }
                                          />
                                        }
                                        label="AI validation"
                                      />
                                    </Box>
                                  )}
                                  {projectSupplier.pre_survey_tool && (
                                    <SupplierQuestions
                                      questions={projectSupplier.questions || []}
                                      name={`input.suppliers.${sIdx}.questions`}
                                      updateQuestions={updateSupplierQuestions}
                                    />
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              <InputLabel sx={{ fontSize: 14, color: COLORS.textMain, mt: 0, mb: 0.5 }}>Add Supplier</InputLabel>
              <Box display="flex" alignItems="flex-start" pb={2}>
                <AutoCompleteDropdown
                  label="Choose supplier"
                  value={suppliers.find((s) => s.id === newSupplierId) || ""}
                  options={suppliers}
                  onChange={(id) => setNewSupplierId(id)}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  placeholder="Choose supplier"
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  startAdornment={
                    !newSupplierId ? (
                      <InputAdornment position="start">
                        <SearchIcon color="action" sx={{ color: "#67676C", width: "20px" }} />
                      </InputAdornment>
                    ) : null
                  }
                  sx={{ width: 300, mr: 2 }}
                />
                <Button
                  className={cx(buttonClasses.blue, buttonClasses.iconButton)}
                  sx={{ "& .MuiSvgIcon-root": { mr: 0 } }}
                  onClick={() => {
                    if (newSupplierId) {
                      arrayHelpers.push({
                        supplier_id: newSupplierId,
                        limit: 0,
                        survey_url: "",
                        opening_tag: "{{",
                        closing_tag: "}}",
                        respondent_key: "",
                        status_key: "",
                        score: 25, // Default Score Threshold to 25
                        rt_proof_of_human_threshold: 60, // Default Roundtable Proof of Human Score Threshold to 60
                        description: "",
                        country_code: "",
                        fields:
                          suppliers
                            .find((sup) => sup.id === newSupplierId)
                            ?.fields.filter((field) => field.required)
                            .map((field) => ({ supplier_field_id: field.id })) || [],
                      });
                      // setNewSupplierId("");
                    } else {
                      notifySimple(["Select an existing Supplier"], "error");
                    }
                  }}
                >
                  <AddIcon /> Add a Supplier
                </Button>
              </Box>
            </>
          )}
        />
      </Box>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#fff",
          borderTop: "1px solid #E4E8D3",
          display: "flex",
          padding: "16px 32px",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        <Button
          onClick={() => navigate("/")}
          variant="outlined"
          className={cx(buttonClasses.grey, buttonClasses.iconButton)}
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit()}
          className={cx(buttonClasses.blue, buttonClasses.iconButton)}
        >
          {isEditMode ? "Save changes" : "Create project"}
        </Button>
      </Box>
    </Box>
  );
};

export default ProjectForm;
